import React, { useState, useEffect, useMemo } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import {
  TextField,
  Typography,
  Box,
  Slider,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  Divider,
  alpha
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import MuiTooltip from '@mui/material/Tooltip';
import Popover from '@mui/material/Popover';
import { ChevronUp, ChevronDown, Sliders, Info, Award } from 'lucide-react';

// Utility function to convert snake_case to Title Case
const toTitleCase = (str) =>
  str
    .replace(/_/g, ' ')
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

// Scores that need inverted colors
const invertColors = ['claim_severity', 'claim_inflation_score', 'risk_affinity', 'accident_risk', 'chronic_condition_score'];

//overrideFinalScore, overrideFinalPay, formulaNote
const ScoreCircularProgress = ({ scores, insuranceType, externalWeights, overrideFixedPay, overrideVariablePay }) => {
  const theme = useTheme();

  // Initial weights (default values) - memoized to prevent recreation on every render
  const initialWeights = useMemo(() => ({
    customer_loyalty_score: 0.5,
    payment_history_score: 1,
    coverage_adequacy_score: 1,
    risk_affinity: 1.0,
    claim_severity: 0.6,
    claim_inflation_score: 0.4,
    accident_risk: insuranceType === 'Motor Insurance' ? 1 : 0.2,
    customer_spending: 0.2,
    credit_reliability: 0.5,
  }), [insuranceType]);

  // State for weights (editable)
  const [weights, setWeights] = useState({});
  // State for weighted sum
  const [weightedSum, setWeightedSum] = useState(0);
  // Maximum possible weighted sum (for normalization)
  const [maxPossibleSum, setMaxPossibleSum] = useState(0);
  // State for fixed pay
  const [fixedPay, setFixedPay] = useState(1000);
  // State for variable pay
  const [variablePay, setVariablePay] = useState(100);

  // Parameter definitions (defaults)
  const paramDefinitions = useMemo(() => ({
    customer_loyalty_score: 'Indicates repeat business and overall satisfaction.',
    payment_history_score: 'Timeliness and consistency of premium payments.',
    coverage_adequacy_score: 'Alignment of coverage with customer needs.',
    risk_affinity: 'Propensity toward higher-risk behavior (lower is better).',
    claim_severity: 'Average severity of claims (lower is better).',
    claim_inflation_score: 'Upward trend in claim costs (lower is better).',
    accident_risk: 'Likelihood of accidents based on profile (lower is better).',
    customer_spending: 'Average customer spend or premium value.',
    credit_reliability: 'Customer’s credit discipline and reliability.',
    lifestyle_score: 'Lifestyle-related health and risk indicators.'
  }), []);

  // Final score breakdown popover state
  const [breakdownAnchor, setBreakdownAnchor] = useState(null);
  const openBreakdown = Boolean(breakdownAnchor);
  const handleOpenBreakdown = (event) => setBreakdownAnchor(event.currentTarget);
  const handleCloseBreakdown = () => setBreakdownAnchor(null);

  // Initialize weights with defaults
  useEffect(() => {
    // Use default weights for all available scores
    const newWeights = {};
    Object.keys(scores).forEach(key => {
      // Allow external override weights when provided (for Bank/NBFC personas)
      if (externalWeights && typeof externalWeights[key] === 'number') {
        newWeights[key] = externalWeights[key];
      } else {
        newWeights[key] = initialWeights[key] ?? 1.0;
      }
    });
    setWeights(newWeights);
  }, [scores, initialWeights, externalWeights]);

  // override fixed and variable pay
  useEffect(() => {
    if (overrideFixedPay && typeof overrideFixedPay === 'number') {
      setFixedPay(overrideFixedPay)
    }

    if (overrideVariablePay && typeof overrideVariablePay === 'number') {
      setVariablePay(overrideVariablePay)
    }
  }, [overrideFixedPay, overrideVariablePay])

  // Calculate weighted sum when scores or weights change
  useEffect(() => {
    if (Object.keys(weights).length === 0) return;

    let sum = 0;
    let maxSum = 0;

    Object.entries(scores).forEach(([key, value]) => {
      const weight = weights[key] ?? 1.0;
      let scoreValue = value;
      if (invertColors.includes(key)) {
        scoreValue = 100 - value;
      }
      sum += scoreValue * weight;
      maxSum += 100 * weight; // Maximum possible value is 100 for each score
    });

    setWeightedSum(sum);
    setMaxPossibleSum(maxSum);
  }, [scores, weights]);

  // Handle weight change
  const handleWeightChange = (key, newValue) => {
    setWeights(prev => ({
      ...prev,
      [key]: newValue
    }));
  };

  // Convert scores object to array for rendering
  const scoreData = Object.entries(scores).map(([name, value]) => ({
    name,
    displayName: toTitleCase(name),
    value: value
  }));

  const getColor = (value, name) => {
    // Color palette
    const colors = {
      high: '#4C84FF', // Blue
      mid: '#FF9F43',  // Orange
      low: '#FF5C75'   // Red
    };

    if (invertColors.includes(name)) {
      // Inverted logic: More score = red, Less score = blue
      if (value < 40) return colors.high;
      if (value < 60) return colors.mid;
      return colors.low;
    } else {
      // Normal logic: More score = blue, Less score = red
      if (value < 40) return colors.low;
      if (value < 60) return colors.mid;
      return colors.high;
    }
  };

  // Calculate normalized weighted score (0-100)
  const normalizedWeightedScore = maxPossibleSum > 0
    ? Math.round((weightedSum / maxPossibleSum) * 100)
    : 0;
  // const displayedFinalScore = typeof overrideFinalScore === 'number' ? overrideFinalScore : normalizedWeightedScore;
  const displayedFinalScore = normalizedWeightedScore;

  // Calculate final pay
  const computedFinalPay = fixedPay + (variablePay * (normalizedWeightedScore / 100));
  // const displayedFinalPay = typeof overrideFinalPay === 'number' ? overrideFinalPay : computedFinalPay;
  const displayedFinalPay = computedFinalPay;

  return (
    <Box sx={{ width: '100%' }}>
      {/* Score Metrics Grid */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', // Smaller min width for cards
        gap: 2, // Reduced gap
        mb: 2
      }}>
        {scoreData.map((entry) => {
          const scoreColor = getColor(entry.value, entry.name);
          return (
            <Card
              key={entry.name}
              elevation={1}
              sx={{
                borderRadius: 2,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-5px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)'
                }
              }}
            >
              <CardContent sx={{ p: 1, pb: 1, pl: 2, pr: 2.5 }}> {/* Reduced padding */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}> {/* Reduced margin bottom */}
                  <Typography variant="subtitle2" fontWeight="600" sx={{ color: 'text.primary' }}> {/* Reduced font size for title */}
                    {entry.displayName}
                  </Typography>
                  <Avatar
                    sx={{
                      width: 32, // Reduced avatar size
                      height: 32, // Reduced avatar size - fixed typo
                      bgcolor: alpha(scoreColor, 0.1),
                      color: scoreColor
                    }}
                  >
                    {invertColors.includes(entry.name) ? <ChevronDown size={18} /> : <ChevronUp size={18} />} {/* Reduced icon size */}
                  </Avatar>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <MuiTooltip title={paramDefinitions[entry.name] || 'Definition to be updated'}>
                    <IconButton size="small">
                      <Info size={14} />
                    </IconButton>
                  </MuiTooltip>
                </Box>

                <Box sx={{ position: 'relative', height: 110, mb: 1.5 }}> {/* Reduced height for pie chart area */}
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: 'Progress', value: entry.value },
                          { name: 'Remaining', value: 100 - entry.value }
                        ]}
                        dataKey="value"
                        startAngle={-270}
                        endAngle={90}
                        innerRadius="65%" // Adjusted innerRadius for smaller pie
                        outerRadius="80%" // Adjusted outerRadius for smaller pie
                        paddingAngle={2}
                        cornerRadius={10}
                      >
                        <Cell key={`cell-0`} fill={scoreColor} />
                        <Cell key={`cell-1`} fill={alpha(theme.palette.grey[300], 0.5)} />
                      </Pie>
                      <Tooltip
                        content={({ payload }) => {
                          if (payload && payload.length) {
                            return (
                              <Box sx={{
                                background: 'rgba(0,0,0,0.8)',
                                color: 'white',
                                p: 1, // Reduced padding for tooltip
                                borderRadius: 1 // Smaller border radius
                              }}>
                                <Typography variant="caption">{payload[0].name}: {payload[0].value}</Typography>
                              </Box>
                            );
                          }
                          return null;
                        }}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    textAlign: 'center'
                  }}>
                    <Typography variant="h5" fontWeight="bold" sx={{ color: scoreColor }}> {/* Reduced font size for score value */}
                      {entry.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      / 100
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}> {/* Reduced gap */}
                  <Sliders size={14} color='grey'/> {/* Smaller icon */}
                  <Typography variant="body2" fontWeight="500" sx={{ fontSize: '0.8rem', color: 'grey' }}> {/* Slightly smaller font for weight text */}
                    Weight: {weights[entry.name]?.toFixed(1) || '1.0'}
                  </Typography>
                </Box>

                <Slider
                  value={weights[entry.name] ?? 1.0}
                  min={0}
                  max={1}
                  step={0.1}
                  onChange={(_, newValue) => handleWeightChange(entry.name, newValue)}
                  valueLabelDisplay="auto"
                  size="small"
                  sx={{
                    color: scoreColor,
                    height: 6, // Reduced slider height
                    '& .MuiSlider-thumb': {
                      height: 10, // Reduced thumb size
                      width: 10,  // Reduced thumb size
                      boxShadow: '0 0 0 5px ' + alpha(scoreColor, 0.2) // Adjusted shadow
                    },
                    '& .MuiSlider-track': {
                      height: 6, // Reduced track height
                    },
                    '& .MuiSlider-rail': { // Fixed typo from MMuiSlider-rail
                      height: 6, // Reduced rail height
                      opacity: 0.3, // Slightly more visible rail
                      backgroundColor: alpha(scoreColor, 0.2)
                    },
                  }}
                />
              </CardContent>
            </Card>
          );
        })}
      </Box>

      {/* Commission and Pay Calculator */}
      <Card
        elevation={3}
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3
        }}
      >
        <Box sx={{
          p: 2,
          bgcolor: getColor(normalizedWeightedScore, 'default_positive'), // Use a non-inverted key
          color: 'white'
        }}>
          {/* Reduced font for "Final Commission Score" title */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="subtitle1" fontWeight="600">Final Commission Score: {displayedFinalScore} / 100 </Typography>
            <MuiTooltip title="Show score breakdown">
              <IconButton size="small" sx={{ color: 'white' }} onClick={handleOpenBreakdown}>
                <Info size={16} />
              </IconButton>
            </MuiTooltip>
          </Box>
          {/* {formulaNote ? (
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {formulaNote}
            </Typography>
          ) : null} */}
        </Box>

        <CardContent sx={{ p: 2.5 }}> {/* Slightly reduced padding */}
          <Grid container spacing={2.5} sx={{ mb: 2.5 }}> {/* Reduced spacing and margin */}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fixed Pay"
                type="number"
                fullWidth
                value={fixedPay}
                onChange={(e) => setFixedPay(Number(e.target.value))}
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px' }}>₹</span>,
                }}
                variant="outlined"
                size="small" // Makes TextField smaller
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Variable Pay"
                type="number"
                fullWidth
                value={variablePay}
                onChange={(e) => setVariablePay(Number(e.target.value))}
                InputProps={{
                  startAdornment: <span style={{ marginRight: '8px' }}>₹</span>,
                }}
                variant="outlined"
                size="small" // Makes TextField smaller
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2
                  }
                }}
              />
            </Grid>
          </Grid>

          <Card sx={{
            p: 2, // Reduced padding
            bgcolor: alpha(getColor(normalizedWeightedScore, 'default_positive'), 0.08),
            borderRadius: 2,
            border: `1px solid ${alpha(getColor(normalizedWeightedScore, 'default_positive'), 0.2)}`
          }}>
            <Grid container alignItems="center" justifyContent="space-between">
              <Grid item xs={12} md={7}>
                {/* Reduced font for formula text */}
                <Typography variant="body2" fontWeight="500" gutterBottom>
                  Final Pay = Fixed Pay + (Variable Pay × Pay Score)
                </Typography>
                {/* Reduced font for formula breakdown */}
                <Typography variant="caption" color="text.secondary">
                  ₹{fixedPay.toFixed(2)} + (₹{variablePay.toFixed(2)} × {(normalizedWeightedScore/100).toFixed(2)})
                </Typography>
              </Grid>
              <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: {xs: 'flex-start', md: 'flex-end'}, mt: {xs: 2, md: 0} }}>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 1.5, // Reduced padding
                  bgcolor: 'white',
                  borderRadius: 2,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.08)'
                }}>
                  {/* Reduced font for final pay amount */}
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: getColor(normalizedWeightedScore, 'default_positive') }}>
                    ₹{displayedFinalPay.toFixed(2)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Card>
        </CardContent>
      </Card>
      <Popover
        open={openBreakdown}
        anchorEl={breakdownAnchor}
        onClose={handleCloseBreakdown}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ 
          sx: { 
            p: 3, 
            borderRadius: 3, 
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
            minWidth: 300
          } 
        }}
      >
        {(() => {
          const rows = Object.entries(scores).map(([key, value]) => {
            let adjusted = value;
            if (invertColors.includes(key)) adjusted = 100 - value;
            const w = weights[key] ?? 1.0;
            return { label: toTitleCase(key), value: adjusted, weight: w, product: adjusted * w };
          });
          const total = rows.reduce((s, r) => s + r.product, 0);
          const maxPossible = rows.reduce((s, r) => s + (r.weight * 100), 0);
          return (
            <Box>
              <Typography variant="h6" fontWeight="700" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Award size={20} />
                Score Breakdown
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {rows.map((r) => (
                <Box key={r.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, p: 1, borderRadius: 1, '&:hover': { bgcolor: 'grey.50' } }}>
                  <Typography variant="body2" fontWeight={500}>{r.label}</Typography>
                  <Typography variant="body2" color="text.secondary">{r.value} × {r.weight.toFixed(1)} = {r.product.toFixed(1)}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 2 }} />
              <Box sx={{ p: 2, bgcolor: 'primary.light', borderRadius: 2 }}>
                <Typography variant="body2" fontWeight={600} color="primary.contrastText">
                  Total: {total.toFixed(1)} / {maxPossible.toFixed(1)} = {Math.round((total / maxPossible) * 100)}
                </Typography>
              </Box>
            </Box>
          );
        })()}
      </Popover>
    </Box>
  );
};

export default ScoreCircularProgress;